import { IkeFieldType } from '@ikegps/core-api';
import type { FormModelField } from '../../form/form.field';
import type { FormModelFieldNestedList } from '../../form/nested.list/form.nested.list';
import { CollectionModelField, ModelMutator } from '../collection.field';
import { registerFieldType } from './field.registry';

export class ModelMutatorNestedList extends ModelMutator<FormModelField, CollectionModelFieldNestedList> {
	/** `null` unsets the field — the callers below rely on it, and the body has always handled it. */
	manual(newText: string[] | null) {
		if (newText == null || !Array.isArray(newText)) {
			this.state.setFieldValue<string[]>(this.field.id, null);
			return;
		}

		const options = this.field.schema.findOptions(newText);
		if (options == null || options.length !== newText.length || options.length === 0) {
			this.state.setFieldValue<string[]>(this.field.id, null);
			return;
		}

		this.state.setFieldValue<string[]>(this.field.id, newText);
	}

	override copy(from: CollectionModelFieldNestedList): boolean {
		if (!from.hasValue()) {
			this.manual(null);
			return true;
		}

		const value = from.value;
		if (this.field.schema.findOptions(value) != null) {
			this.manual(value);
			return true;
		}

		return false;
	}

	// Copies values from another Nested List, but only those that exist in the current field's schema.
	override truncatedCopy(from: CollectionModelFieldNestedList): boolean {
		if (!from.hasValue()) {
			this.manual(null);
			return true;
		}

		const validValues = from.value.filter((v) => {
			const found = this.field.schema.findOptions([v]);
			return found && found.length === 1;
		});

		if (validValues.length > 0) {
			this.manual(validValues);
			return true;
		}
		return false;
	}
}

export class CollectionModelFieldNestedList extends CollectionModelField<FormModelFieldNestedList, string[]> {
	static TYPE: IkeFieldType = IkeFieldType.NESTED_LIST;
	static isNestedListField(f: CollectionModelField): f is CollectionModelFieldNestedList {
		return f.schema.type === CollectionModelFieldNestedList.TYPE;
	}

	static parse(text: string): string[] | null {
		if (typeof text !== 'string') {
			return null;
		}
		return text.split(' > ');
	}

	private _Mutator: ModelMutatorNestedList;
	get Mutator() {
		if (this._Mutator == null) {
			this._Mutator = new ModelMutatorNestedList(this);
		}
		return this._Mutator;
	}

	override get value(): string[] {
		return this.stateValue || [];
	}

	override get displayValue(): string {
		return this.value.join(' > ');
	}

	get color(): string | null {
		for (const option of this.schema.options.values) {
			if (option.value === this.value[0]) {
				return option.color ?? null;
			}
		}

		return null;
	}

	hasValue(): boolean {
		return this.value.length > 0;
	}
}

registerFieldType(
	IkeFieldType.NESTED_LIST,
	(parent, schema) => new CollectionModelFieldNestedList(parent, <FormModelFieldNestedList>schema)
);
